import React, { useCallback, useEffect, useState } from 'react';
import { StytchSDKAPIError } from '@stytch/core/public';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import Button from 'fe-design-base/molecules/Button/Button';
import { Form, Formik, FormikHelpers } from 'formik';

import { signOutUser, updatePassword } from 'features/authentication/actions';
import NewPasswordField from 'features/authentication/NewPasswordField';
import PasswordVerificationList from 'features/authentication/PasswordVerificationList';
import { isNewPasswordValid } from 'features/authentication/PasswordVerificationList/helpers';
import {
  AUTH_EXPIRATION_MS,
  SIGN_IN_PATH,
} from 'features/authentication/SignIn/constants';
import HeaderLayout from 'features/authentication/SignIn/HeaderLayout';
import { mapResetPasswordErrorToMessage } from 'features/authentication/SignIn/helpers';
import { ErrorTrackingProps } from 'features/authentication/SignIn/types';

import { toI18n } from 'util/i18n';
import { useStytch } from 'util/stytch';

interface FormValues {
  password: string;
}

const NewPasswordForm = ({ onError }: ErrorTrackingProps) => {
  const [alertError, setAlertError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const stytchClient = useStytch();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setAlertError(
        toI18n('authentication.errors.session_too_old_to_reset_password')
      );
    }, AUTH_EXPIRATION_MS);

    return () => clearTimeout(timeoutId);
  });

  const handleStytchError = useCallback(
    (error: StytchSDKAPIError) => {
      const errorMessage = mapResetPasswordErrorToMessage(error);
      setAlertError(errorMessage);
      onError(errorMessage);
    },
    [onError]
  );

  const handleHBError = useCallback(
    (error: any) => {
      let errorMessage = toI18n('authentication.errors.reset_password_with_hb');

      error.response
        .json()
        .then((body: { errors?: { password?: string[] | undefined } }) => {
          const passwordErrors = body.errors?.password;
          if (
            passwordErrors?.length &&
            passwordErrors[0].includes('was already taken in the past!')
          ) {
            errorMessage = toI18n('authentication.errors.taken');
          }
          setAlertError(errorMessage);
          onError(errorMessage);
        });
    },
    [onError]
  );

  const handleSignOutUser = useCallback(() => {
    // Note: Using .finally (vs .then) here since signOutUser returns a 204 response
    signOutUser().finally(() => {
      window.location.href = SIGN_IN_PATH;
    });
  }, []);

  const handleSubmit = useCallback(
    async (
      { password }: FormValues,
      { setSubmitting }: FormikHelpers<FormValues>
    ) => {
      setAlertError('');
      setIsLoading(true);
      try {
        // Updates password with Stytch
        await stytchClient.resetPasswordBySession(password);
        // Updates password with Homebase backend
        await updatePassword({ password });
        handleSignOutUser();
      } catch (error: any) {
        if (error.error_type) {
          handleStytchError(error);
        } else {
          handleHBError(error);
        }

        setSubmitting(false);
        setIsLoading(false);
      }
    },
    [handleHBError, handleSignOutUser, handleStytchError, stytchClient]
  );

  return (
    <HeaderLayout
      backLinkTo={SIGN_IN_PATH}
      title="authentication.forgot_password.new.title"
      description="authentication.forgot_password.new.description"
      dataTestId="NewPasswordForm"
      alertError={alertError}
    >
      <Formik onSubmit={handleSubmit} initialValues={{ password: '' }}>
        {({ values }) => (
          <Form role="form">
            <Box mt={16} mb={12}>
              <NewPasswordField />
            </Box>
            <Text variant="heading4">
              {toI18n('authentication.forgot_password.new.requirement_title')}:
            </Text>
            <Box mt={20} column gap={32}>
              <PasswordVerificationList password={values.password} />
              <Button
                type="submit"
                isLoading={isLoading}
                uxElement="submit password button"
                disabled={!isNewPasswordValid(values.password)}
                fullWidth
              >
                {toI18n('authentication.forgot_password.new.submit_button')}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </HeaderLayout>
  );
};

export default NewPasswordForm;
